import type { TextDataType } from '@/datas/LocaleData';

export type NotificationCopy = {
    notif_page_title: string;
    notif_empty: string;
    notif_mark_all_read: string;
    notif_recharge_success_title: string;
    notif_recharge_success_body: string;
    notif_subscription_benefit_title: string;
    notif_subscription_benefit_body: string;
    notif_puzzle_approved_title: string;
    notif_puzzle_approved_body: string;
    notif_puzzle_likes_title: string;
    notif_puzzle_likes_body: string;
    notif_welcome_title: string;
    notif_welcome_body: string;
    notif_puzzle_rejected_title: string;
    notif_puzzle_rejected_body: string;
    notif_puzzle_rejected_edit: string;
    notif_rejected_reason_title: string;
    notif_rejected_reason_soup: string;
    notif_rejected_reason_truth: string;
    notif_rejected_reason_author_name: string;
    notif_rejected_reason_origin_type: string;
    notif_new_version_title: string;
    notif_new_version_body: string;
};

const en: NotificationCopy = {
    notif_page_title: 'Notifications', notif_empty: 'No notifications yet', notif_mark_all_read: 'Mark all read',
    notif_recharge_success_title: 'Recharge Successful', notif_recharge_success_body: 'Added {amount} credits. Balance: {balance} credits',
    notif_subscription_benefit_title: 'Lite Benefits Credited', notif_subscription_benefit_body: 'Your Lite subscription delivered {amount} SP. Balance: {balance} SP.',
    notif_puzzle_approved_title: 'Puzzle Approved', notif_puzzle_approved_body: 'Your puzzle "{title}" has been approved and is now live',
    notif_puzzle_likes_title: "Yesterday's Likes", notif_puzzle_likes_body: '"{title}" got {count} likes & interactions yesterday. Take a look!',
    notif_welcome_title: 'Welcome to Scenigma', notif_welcome_body: "Thanks for joining Scenigma! We've given you 50 starter SP — enjoy the mysteries!",
    notif_puzzle_rejected_title: 'Puzzle Review Failed', notif_puzzle_rejected_body: 'Your puzzle "{title}" was rejected: {reason}', notif_puzzle_rejected_edit: 'Edit Puzzle',
    notif_rejected_reason_title: 'Title violates guidelines', notif_rejected_reason_soup: 'Puzzle content violates guidelines', notif_rejected_reason_truth: 'Answer violates guidelines', notif_rejected_reason_author_name: 'Author name violates guidelines', notif_rejected_reason_origin_type: 'Incorrect original/repost type',
    notif_new_version_title: 'New Version Available', notif_new_version_body: 'Scenigma {version} is out with new content and improvements.',
};

const subscriptionBenefitCopy: Record<string, Pick<NotificationCopy, 'notif_subscription_benefit_title' | 'notif_subscription_benefit_body'>> = {
    'zh-Hans': {
        notif_subscription_benefit_title: 'Lite 订阅权益已到账',
        notif_subscription_benefit_body: '本期 {amount} SP 已到账，当前余额 {balance} SP。',
    },
    'zh-Hant': {
        notif_subscription_benefit_title: 'Lite 訂閱權益已到帳',
        notif_subscription_benefit_body: '本期 {amount} SP 已到帳，目前餘額 {balance} SP。',
    },
    ja: {
        notif_subscription_benefit_title: 'Lite特典を付与しました',
        notif_subscription_benefit_body: '今期の{amount} SPを付与しました。残高：{balance} SP。',
    },
    ko: {
        notif_subscription_benefit_title: 'Lite 혜택 지급 완료',
        notif_subscription_benefit_body: '이번 기간의 {amount} SP가 지급되었습니다. 잔액: {balance} SP.',
    },
    es: {
        notif_subscription_benefit_title: 'Beneficios Lite acreditados',
        notif_subscription_benefit_body: 'Se añadieron {amount} SP de este período. Saldo: {balance} SP.',
    },
    th: {
        notif_subscription_benefit_title: 'รับสิทธิประโยชน์ Lite แล้ว',
        notif_subscription_benefit_body: 'เพิ่ม {amount} SP สำหรับรอบนี้แล้ว ยอดคงเหลือ: {balance} SP',
    },
    id: {
        notif_subscription_benefit_title: 'Benefit Lite telah masuk',
        notif_subscription_benefit_body: '{amount} SP untuk periode ini telah ditambahkan. Saldo: {balance} SP.',
    },
    de: {
        notif_subscription_benefit_title: 'Lite-Vorteile gutgeschrieben',
        notif_subscription_benefit_body: 'Für diesen Zeitraum wurden {amount} SP gutgeschrieben. Kontostand: {balance} SP.',
    },
    ru: {
        notif_subscription_benefit_title: 'Преимущества Lite начислены',
        notif_subscription_benefit_body: 'За текущий период начислено {amount} SP. Баланс: {balance} SP.',
    },
    vi: {
        notif_subscription_benefit_title: 'Đã cộng quyền lợi Lite',
        notif_subscription_benefit_body: 'Đã cộng {amount} SP cho kỳ này. Số dư: {balance} SP.',
    },
};

type LegacyNotificationCopy = Omit<NotificationCopy, 'notif_subscription_benefit_title' | 'notif_subscription_benefit_body'>;

const dictionaries: Record<string, LegacyNotificationCopy> = {
    en,
    zh: {
        notif_page_title: '消息通知', notif_empty: '暂无消息', notif_mark_all_read: '全部已读',
        notif_recharge_success_title: '充值成功', notif_recharge_success_body: '已成功充值 {amount} 积分，当前余额 {balance} 积分',
        notif_puzzle_approved_title: '谜题审核通过', notif_puzzle_approved_body: '你的谜题「{title}」已通过审核，现已对外开放',
        notif_puzzle_likes_title: '昨日获赞', notif_puzzle_likes_body: '「{title}」昨日收到 {count} 次点赞与互动，快去看看吧',
        notif_welcome_title: '欢迎来到境谜', notif_welcome_body: '感谢加入境谜！已送上初始50SP点，尽情解谜吧！',
        notif_puzzle_rejected_title: '谜题审核未通过', notif_puzzle_rejected_body: '你的谜题「{title}」审核未通过，{reason}', notif_puzzle_rejected_edit: '重新编辑',
        notif_rejected_reason_title: '标题不符合规范', notif_rejected_reason_soup: '谜题内容不符合规范', notif_rejected_reason_truth: '谜底不符合规范', notif_rejected_reason_author_name: '作者名不符合规范', notif_rejected_reason_origin_type: '原创/转载类型不正确',
        notif_new_version_title: '发现新版本', notif_new_version_body: '境谜 {version} 已发布，带来了新内容和优化。',
    },
    ja: {
        notif_page_title: '通知', notif_empty: '通知はありません', notif_mark_all_read: 'すべて既読',
        notif_recharge_success_title: 'チャージ完了', notif_recharge_success_body: '{amount}クレジットを追加しました。残高：{balance}クレジット',
        notif_puzzle_approved_title: 'パズル審査通過', notif_puzzle_approved_body: 'あなたのパズル「{title}」が承認され、公開されました',
        notif_puzzle_likes_title: '昨日のいいね', notif_puzzle_likes_body: '「{title}」昨日{count}件のいいね・インタラクションがありました。',
        notif_welcome_title: '境謎へようこそ', notif_welcome_body: '初期50SPをプレゼントしました。謎解きを楽しんでください！',
        notif_puzzle_rejected_title: 'パズル審査不合格', notif_puzzle_rejected_body: 'パズル「{title}」が審査不合格になりました：{reason}', notif_puzzle_rejected_edit: '再編集',
        notif_rejected_reason_title: 'タイトルが規定に違反しています', notif_rejected_reason_soup: 'パズル内容が規定に違反しています', notif_rejected_reason_truth: '答えが規定に違反しています', notif_rejected_reason_author_name: '作者名が規定に違反しています', notif_rejected_reason_origin_type: 'オリジナル/転載の種別が正しくありません',
        notif_new_version_title: '新バージョンが利用可能', notif_new_version_body: 'Scenigma {version} がリリースされました。新コンテンツと改善が含まれています。',
    },
    es: {
        notif_page_title: 'Notificaciones', notif_empty: 'Sin notificaciones', notif_mark_all_read: 'Marcar todo leído',
        notif_recharge_success_title: 'Recarga exitosa', notif_recharge_success_body: 'Se añadieron {amount} créditos. Saldo: {balance} créditos',
        notif_puzzle_approved_title: 'Acertijo aprobado', notif_puzzle_approved_body: 'Tu acertijo "{title}" fue aprobado y ya está disponible',
        notif_puzzle_likes_title: 'Me gusta de ayer', notif_puzzle_likes_body: '"{title}" tuvo {count} me gusta e interacciones ayer.',
        notif_welcome_title: 'Bienvenido a Scenigma', notif_welcome_body: 'Te hemos dado 50 SP iniciales. ¡Disfruta los misterios!',
        notif_puzzle_rejected_title: 'Acertijo rechazado', notif_puzzle_rejected_body: 'Tu acertijo "{title}" fue rechazado: {reason}', notif_puzzle_rejected_edit: 'Editar acertijo',
        notif_rejected_reason_title: 'El título incumple las normas', notif_rejected_reason_soup: 'El contenido incumple las normas', notif_rejected_reason_truth: 'La respuesta incumple las normas', notif_rejected_reason_author_name: 'El nombre de autor incumple las normas', notif_rejected_reason_origin_type: 'Tipo original/republicado incorrecto',
        notif_new_version_title: 'Nueva versión disponible', notif_new_version_body: 'Scenigma {version} ya está disponible con nuevo contenido y mejoras.',
    },
    th: {
        notif_page_title: 'การแจ้งเตือน', notif_empty: 'ยังไม่มีการแจ้งเตือน', notif_mark_all_read: 'ทำเครื่องหมายว่าอ่านทั้งหมด',
        notif_recharge_success_title: 'เติมเงินสำเร็จ', notif_recharge_success_body: 'เพิ่ม {amount} เครดิต ยอดคงเหลือ: {balance} เครดิต',
        notif_puzzle_approved_title: 'ปริศนาผ่านการตรวจสอบ', notif_puzzle_approved_body: 'ปริศนา "{title}" ของคุณได้รับการอนุมัติและเผยแพร่แล้ว',
        notif_puzzle_likes_title: 'ไลก์เมื่อวาน', notif_puzzle_likes_body: 'ปริศนา "{title}" ได้รับไลก์และอินเทอร์แอคชัน {count} ครั้งเมื่อวาน',
        notif_welcome_title: 'ยินดีต้อนรับสู่ Scenigma', notif_welcome_body: 'เราให้ SP เริ่มต้น 50 แต้มแล้ว สนุกกับการแก้ปริศนาได้เลย!',
        notif_puzzle_rejected_title: 'ปริศนาไม่ผ่านการตรวจสอบ', notif_puzzle_rejected_body: 'ปริศนา "{title}" ของคุณไม่ผ่านการตรวจสอบ: {reason}', notif_puzzle_rejected_edit: 'แก้ไขปริศนา',
        notif_rejected_reason_title: 'ชื่อเรื่องไม่เป็นไปตามกฎ', notif_rejected_reason_soup: 'เนื้อหาปริศนาไม่เป็นไปตามกฎ', notif_rejected_reason_truth: 'คำตอบไม่เป็นไปตามกฎ', notif_rejected_reason_author_name: 'ชื่อผู้แต่งไม่เป็นไปตามกฎ', notif_rejected_reason_origin_type: 'ประเภทต้นฉบับ/แปลผิด',
        notif_new_version_title: 'มีเวอร์ชันใหม่แล้ว', notif_new_version_body: 'Scenigma {version} มาพร้อมเนื้อหาและการปรับปรุงใหม่',
    },
    id: {
        notif_page_title: 'Notifikasi', notif_empty: 'Belum ada notifikasi', notif_mark_all_read: 'Tandai semua dibaca',
        notif_recharge_success_title: 'Isi ulang berhasil', notif_recharge_success_body: 'Ditambahkan {amount} kredit. Saldo: {balance} kredit',
        notif_puzzle_approved_title: 'Teka-teki disetujui', notif_puzzle_approved_body: 'Teka-teki "{title}" kamu telah disetujui dan sudah live',
        notif_puzzle_likes_title: 'Suka kemarin', notif_puzzle_likes_body: 'Teka-teki "{title}" dapat {count} suka & interaksi kemarin.',
        notif_welcome_title: 'Selamat datang di Scenigma', notif_welcome_body: 'Kami memberimu 50 SP awal. Selamat memecahkan misteri!',
        notif_puzzle_rejected_title: 'Teka-teki ditolak', notif_puzzle_rejected_body: 'Teka-teki "{title}" kamu ditolak: {reason}', notif_puzzle_rejected_edit: 'Edit teka-teki',
        notif_rejected_reason_title: 'Judul melanggar panduan', notif_rejected_reason_soup: 'Konten teka-teki melanggar panduan', notif_rejected_reason_truth: 'Jawaban melanggar panduan', notif_rejected_reason_author_name: 'Nama penulis melanggar panduan', notif_rejected_reason_origin_type: 'Tipe asli/posting ulang salah',
        notif_new_version_title: 'Versi baru tersedia', notif_new_version_body: 'Scenigma {version} hadir dengan konten dan peningkatan baru.',
    },
};

export function getNotificationCopy(languageCode: string, langData: TextDataType): NotificationCopy {
    const normalized = languageCode.toLowerCase();
    const prefix = normalized.split('-')[0];
    const subscriptionLocale = prefix === 'zh'
        ? (/(?:tw|hant|hk|mo)/.test(normalized) ? 'zh-Hant' : 'zh-Hans')
        : prefix;
    const base = {
        ...(dictionaries[prefix] ?? en),
        ...(subscriptionBenefitCopy[subscriptionLocale] ?? {
            notif_subscription_benefit_title: en.notif_subscription_benefit_title,
            notif_subscription_benefit_body: en.notif_subscription_benefit_body,
        }),
    };
    const runtime = langData as unknown as Partial<NotificationCopy>;
    const localized = { ...base };
    for (const key of Object.keys(base) as (keyof NotificationCopy)[]) {
        if (typeof runtime[key] === 'string') localized[key] = runtime[key];
    }
    return localized;
}
